/**
 * CompletionStep Component
 *
 * Final step of enrollment: Summary and next steps.
 *
 * FERP-PII-052: Enrollment completion confirmation.
 */

import React from 'react';
import {
  Stack,
  Text,
  Button,
  Alert,
  ThemeIcon,
  List,
  Paper,
  Group,
} from '@mantine/core';
import {
  IconCheck,
  IconShieldCheck,
  IconFingerprint,
  IconKey,
  IconSettings,
  IconArrowRight,
} from '@tabler/icons-react';

export interface CompletionStepProps {
  /** Called when user clicks Continue */
  onComplete: () => void;
}

export const CompletionStep: React.FC<CompletionStepProps> = ({
  onComplete,
}) => {
  return (
    <Stack gap="xl" mt="md" ta="center">
      {/* Success icon */}
      <ThemeIcon color="green" size={80} radius="xl">
        <IconShieldCheck size={48} />
      </ThemeIcon>

      {/* Success message */}
      <Stack gap={4} ta="center">
        <Text size="xl" fw={600}>
          You&apos;re All Set!
        </Text>
        <Text size="sm" c="dimmed" ta="center" maw={400}>
          Your biometric protection is now active. Only you can access your sensitive information.
        </Text>
      </Stack>

      {/* What's protected */}
      <Paper p="md" withBorder w="100%">
        <Text size="sm" fw={500} mb="sm">
          What&apos;s Now Protected
        </Text>
        <List size="sm" spacing="xs">
          <List.Item
            icon={
              <ThemeIcon color="green" size={20} radius="xl">
                <IconCheck size={12} />
              </ThemeIcon>
            }
          >
            Social Security Number (SSN)
          </List.Item>
          <List.Item
            icon={
              <ThemeIcon color="green" size={20} radius="xl">
                <IconCheck size={12} />
              </ThemeIcon>
            }
          >
            Bank Account Information
          </List.Item>
          <List.Item
            icon={
              <ThemeIcon color="green" size={20} radius="xl">
                <IconCheck size={12} />
              </ThemeIcon>
            }
          >
            Tax Identification Numbers
          </List.Item>
          <List.Item
            icon={
              <ThemeIcon color="green" size={20} radius="xl">
                <IconCheck size={12} />
              </ThemeIcon>
            }
          >
            Credit Card Details
          </List.Item>
        </List>
      </Paper>

      {/* How it works reminder */}
      <Alert color="blue" icon={<IconFingerprint size={16} />} w="100%">
        <Text size="sm">
          <strong>How It Works:</strong> When you need to view protected information, you&apos;ll be
          prompted to use your fingerprint or face. No one else—not even administrators—can
          access this data without your biometric.
        </Text>
      </Alert>

      {/* Security settings link */}
      <Paper p="md" withBorder w="100%">
        <Group justify="space-between">
          <Group gap="sm">
            <ThemeIcon color="gray" size={32} radius="md" variant="light">
              <IconSettings size={18} />
            </ThemeIcon>
            <Stack gap={0}>
              <Text size="sm" fw={500}>Security Settings</Text>
              <Text size="xs" c="dimmed">Manage devices, recovery options</Text>
            </Stack>
          </Group>
          <Button
            variant="subtle"
            size="xs"
            rightSection={<IconArrowRight size={14} />}
          >
            View Settings
          </Button>
        </Group>
      </Paper>

      {/* Continue button */}
      <Button
        onClick={onComplete}
        size="lg"
        fullWidth
        rightSection={<IconArrowRight size={18} />}
      >
        Continue to Dashboard
      </Button>
    </Stack>
  );
};
